/**
 * The field. Each frame: advance the playback clock, hand the events it crossed to the effects,
 * then put every sprite where its track says it is at that time. Positions come only from the
 * sim's event log (CLAUDE.md rule 2); Phaser tweens never own one. Poses (idle, run, hold) are read
 * off the same log; they pick an animation, never a position.
 */
import Phaser from 'phaser';
import type { Combatant, DefenseSlot, OffenseSlot, PlayEvent, Spot, UnitId } from '@tdmons/sim';
import { color, num, prefersReducedMotion } from '../ui/tokens';
import { followScroll, formationScroll, type Point } from './camera';
import { drawField } from './fieldTexture';
import { Fx } from './fx';
import { cameraMargins, fieldGeometry, type FieldGeometry } from './geometry';
import { Clock } from './playback';
import type { FieldView } from './playView';
import { buildPoses, poseAt, type Pose, type Poses } from './pose';
import { frameAt } from './sheet';
import spriteIndex from './spriteIndex.json';
import { queueSprites, spritePlan, type SpriteIndex, type SpriteRequest } from './spriteLoader';
import {
  ensureFxTextures,
  makeBall,
  makeUnits,
  setHp,
  shapeSize,
  type UnitSprite,
} from './sprites';
import {
  EventCursor,
  ballAt,
  buildTimeline,
  flightAt,
  positionAt,
  type Timeline,
  type Track,
} from './timeline';

/** The name plate over a tapped Pokémon (HTML, `.win-light`). */
export interface NamePlate {
  /** Show this name; the plate hides itself after `--t-name-plate`. */
  show(name: string): void;
  /** Put the plate over this CSS-pixel point of the field (the top of the sprite). */
  moveTo(x: number, y: number): void;
  readonly open: boolean;
}

export interface FieldSceneData {
  /** A snap, or a formation only (its log is the snap event alone): everyone stands in it. */
  view: FieldView;
  /** Freeze at this sim time instead of playing to the whistle (`?t=`). */
  stopAt: number | null;
  /** Playback speed: 1 or 2. */
  speed: number;
  /** Sim times the play stops at until `release` (lines the player reads with a tap). */
  holds: readonly number[];
  /** Jump straight to the whistle (the speed pill's skip). */
  skip?: boolean;
  /** An event has played (or been skipped past); `index` is its place in the log. */
  onEvent(index: number): void;
  onBanner(text: string): void;
  onWhistle(e: Extract<PlayEvent, { type: 'whistle' }>): void;
  namePlate: NamePlate;
  /** Device px of the canvas's bottom the scorebug covers; the camera keeps the ball above it. */
  safeInset(): number;
}

/** Everyone stands in the formation this long before the snap. Presentation only. */
const LEAD_IN_SECONDS = 0.8;
/** Peak height of a throw, in yards per yard thrown, and its cap. Drawing only. */
const ARC_PER_YARD = 0.2;
const ARC_MAX_YARDS = 4;
/** Camera smoothing a frame; 1 snaps straight to the ball under reduced motion (camera.ts). */
const FOLLOW_LERP = 0.12;
/** Idle loops play at their own speed; a running Pokémon's plays twice as fast. */
const LOOP_RATE: Record<Pose, number> = { idle: 1, run: 2, hold: 0 };
/** A running Pokémon bobs one sprite pixel, this many times per sim second. */
const BOB_PER_SECOND = 6;
/** Depth bands: the field under 0, Pokémon from 1 to 2 (lower on screen in front), the ball over. */
const UNIT_DEPTH = 1;

export class FieldScene extends Phaser.Scene {
  private play!: FieldSceneData;
  private view!: FieldView;
  /** Set by skip(): the events it jumps over show their end states but no effects. */
  private skipping = false;

  private geo!: FieldGeometry;
  private timeline!: Timeline;
  private poses!: Poses;
  private clock!: Clock;
  private cursor!: EventCursor;
  private fx!: Fx;
  private sprites!: Map<UnitId, UnitSprite>;
  private ball!: { ball: Phaser.GameObjects.Image; shadow: Phaser.GameObjects.Image };
  private fieldParts!: Phaser.GameObjects.GameObject[];
  /** The line of scrimmage and the line to gain: the only field parts that change per snap. */
  private lines!: Phaser.GameObjects.GameObject[];
  private reduced = false;
  /** What the camera follows: the ball on the ground. */
  private readonly ballProxy = { x: 0, y: 0 };
  /** Sheets the rosters may need later (the other facing); fetched after the first snap starts. */
  private later: SpriteRequest[] = [];
  /** The Pokémon whose name plate is up. */
  private plateUnit: UnitId | null = null;
  /** Each Pokémon's HP as the bars show it: the snap's start, then every `hp` event played. */
  private hpNow = new Map<UnitId, number>();
  private scroll: Point = { x: 0, y: 0 };
  private lerp = FOLLOW_LERP;

  constructor() {
    super('field');
  }

  /** Runs on every start and restart (Phaser reuses the scene instance), so reset everything. */
  init(data: FieldSceneData): void {
    this.play = data;
    this.view = data.view;
    this.skipping = false;
    this.sprites = new Map();
    this.fieldParts = [];
    this.lines = [];
    this.plateUnit = null;
    this.hpNow = new Map();
    this.scroll = { x: 0, y: 0 };
  }

  /** Only the sheets the fourteen on the field show right now; nothing else holds up the snap. */
  preload(): void {
    const plan = spritePlan(this.view.before, spriteIndex as SpriteIndex);
    this.later = plan.later;
    queueSprites(this.load, plan.now);
  }

  /**
   * The next snap. With the same fourteen Pokémon on the field (every snap of a drive) the scene
   * resets in place, which costs nothing; a restart would rebuild every sprite and drop frames on
   * a phone. Anything else restarts the scene (and preloads any sheets it does not have yet).
   */
  nextSnap(data: FieldSceneData): void {
    const roster = (v: FieldView) => v.units.map((u) => `${u.unit}=${u.creatureId}`).join();
    // Not created yet (a click during boot), or different Pokémon: start over.
    if (this.clock === undefined || roster(data.view) !== roster(this.view)) {
      this.scene.restart(data);
      return;
    }
    this.play = data;
    this.view = data.view;
    this.skipping = false;
    this.tweens.killAll();
    this.time.removeAllEvents();
    for (const s of this.sprites.values()) {
      for (const extra of s.container.list.slice(s.parts)) extra.destroy();
      s.body.setScale(s.scale).setAngle(0).setTint(s.tint).setTintMode(Phaser.TintModes.MULTIPLY);
    }
    this.ball.ball.setAlpha(1);
    this.begin();
  }

  /**
   * The speed pill can be tapped while the scene is still loading (the formation goes up during
   * the opening notes, slice 8b): there is no clock yet, so the speed waits in the data for create.
   */
  setSpeed(speed: number): void {
    if (this.play) this.play = { ...this.play, speed };
    if (this.clock) this.clock.speed = speed;
  }

  /** A held line has been read: let the play go on past sim time `t`. */
  release(t: number): void {
    this.clock?.release(t);
  }

  /** Jump to the whistle: every event left is handed out at once, without effects. */
  skip(): void {
    if (!this.clock) {
      // Still loading: begin() skips once it runs.
      if (this.play) this.play = { ...this.play, skip: true };
      return;
    }
    this.skipping = true;
    this.clock.skip();
  }

  create(): void {
    this.reduced = prefersReducedMotion();
    this.lerp = this.reduced ? 1 : FOLLOW_LERP;
    this.layout();
    this.buildUnits();

    this.scale.on(Phaser.Scale.Events.RESIZE, this.onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.scale.off(Phaser.Scale.Events.RESIZE, this.onResize, this),
    );
    this.begin();

    // Now the play is running: fetch what the rosters may need next, in the background.
    if (this.later.length > 0) {
      queueSprites(this.load, this.later);
      this.load.start();
    }
  }

  /** Fills the same map the effects hold, so a rebuild (a resize) keeps them pointed at it. */
  private buildUnits(): void {
    for (const s of this.sprites.values()) s.container.destroy();
    this.sprites.clear();
    for (const [unit, s] of makeUnits(this, this.view, this.geo, (u) => this.tapped(u))) {
      this.sprites.set(unit, s);
    }
    this.ball?.ball.destroy();
    this.ball?.shadow.destroy();
    this.ball = makeBall(this, shapeSize(this.geo.pxPerYard));
    for (const [unit, hp] of this.hpNow) this.showHp(unit, hp, false);
  }

  /** A Pokémon's HP bar at hp, drained over --t-hp-drain when `animate`. */
  private showHp(unit: UnitId, hp: number, animate: boolean): void {
    const s = this.sprites.get(unit);
    if (!s) return;
    this.hpNow.set(unit, hp);
    setHp(this, s, hp, this.combatant(unit).stats.hp, animate && !this.reduced);
  }

  /**
   * Until the ball moves (a formation, and a snap's lead-in), frame the whole formation; then ease
   * toward the ball, inside the band the scorebug leaves (camera.ts).
   */
  private follow(lerp: number): void {
    const view = { w: this.scale.width, h: this.scale.height, insetBottom: this.play.safeInset() };
    const world = { w: this.geo.width, h: this.geo.height };
    const margin = cameraMargins(this.geo, num('hp-bar-field'));
    const t = this.clock.t;
    this.scroll =
      t <= 0
        ? formationScroll(
            this.ballProxy,
            [...this.timeline.units.values()].map((track) => this.geo.toPx(positionAt(track, t))),
            view,
            world,
            margin,
          )
        : followScroll(this.scroll, this.ballProxy, view, world, margin, lerp);
    this.cameras.main.setScroll(this.scroll.x, this.scroll.y);
  }

  /** Per-snap state: the timeline, the clock, the effects, the lines, the camera on the ball. */
  private begin(): void {
    this.timeline = buildTimeline(this.view.events);
    this.poses = buildPoses(this.view.events);
    this.clock = new Clock(this.timeline.end, LEAD_IN_SECONDS, this.play.stopAt);
    this.clock.speed = this.play.speed;
    this.clock.holdAt(this.play.holds);
    this.cursor = new EventCursor(this.view.events);
    this.fx = new Fx({
      scene: this,
      sprites: this.sprites,
      reduced: this.reduced,
      dpr: this.geo.dpr,
      textures: ensureFxTextures(this, shapeSize(this.geo.pxPerYard)),
      toPx: (s) => this.geo.toPx(s),
      moveName: (unit, moveId) =>
        this.combatant(unit).moves.find((m) => m.id === moveId)?.name ?? moveId,
      hitStop: (ms) => this.clock.hitStop(ms),
      banner: (text) => this.play.onBanner(text),
      deadBall: () => this.ball.ball.setAlpha(0.5),
      onWhistle: (e) => this.play.onWhistle(e),
      safeInset: () => this.play.safeInset(),
    });
    this.drawLines();
    this.hpNow.clear();
    for (const unit of this.sprites.keys()) this.showHp(unit, this.combatant(unit).hp, false);
    this.draw(this.clock.t, 0);
    this.follow(1);
    if (this.play.skip) this.skip();
  }

  override update(_time: number, delta: number): void {
    const t = this.clock.tick(delta);
    const first = this.cursor.position;
    this.cursor.advance(t).forEach((e, k) => {
      if (e.type === 'hp') this.showHp(e.unit, e.hp, !this.skipping);
      this.fx.play(e, this.skipping);
      this.play.onEvent(first + k);
    });
    this.draw(t, delta);
    this.follow(this.lerp);
  }

  private tapped(unit: UnitId): void {
    const s = this.sprites.get(unit);
    if (!s) return;
    this.plateUnit = unit;
    this.play.namePlate.show(s.name);
    this.placePlate();
  }

  /** Keep the plate over the top of the tapped sprite while it is up (it may be running). */
  private placePlate(): void {
    if (this.plateUnit === null || !this.play.namePlate.open) return;
    const s = this.sprites.get(this.plateUnit);
    if (!s) return;
    const view = this.cameras.main.worldView;
    const { dpr } = this.geo;
    this.play.namePlate.moveTo(
      (s.container.x - view.x) / dpr,
      (s.container.y + s.top - view.y) / dpr,
    );
  }

  private combatant(unit: UnitId): Combatant {
    const [side, slot] = unit.split(':') as ['off' | 'def', string];
    const { before } = this.view;
    return side === 'off'
      ? before.offense.lineup[slot as OffenseSlot]
      : before.defense.lineup[slot as DefenseSlot];
  }

  /** The line of scrimmage and the line to gain, from the state before the snap. */
  private drawLines(): void {
    for (const p of this.lines) p.destroy();
    const { height, pxPerYard } = this.geo;
    const { ballOn, toGo } = this.view.before;
    const line = (yard: number, c: number) =>
      this.add
        .rectangle(
          this.geo.toPx([yard, 0]).x,
          height / 2,
          Math.max(2, Math.round(pxPerYard / 6)),
          height,
          c,
        )
        .setDepth(-8);
    this.lines = [
      line(ballOn, color('field-los')),
      line(Math.min(100, ballOn + toGo), color('field-to-gain')),
    ];
  }

  /**
   * The field and the yard numbers at the canvas's size and pixel ratio. The camera has no Phaser
   * bounds: `follow` keeps it on the field and the ball above the scorebug.
   */
  private layout(): void {
    for (const p of this.fieldParts) p.destroy();
    this.geo = fieldGeometry(this.scale.width, 1 / this.scale.zoom);
    this.fieldParts = drawField(this, this.geo);
    this.cameras.main.setBackgroundColor(color('ground'));
  }

  private onResize(): void {
    const before = this.geo;
    this.layout();
    this.drawLines();
    // Sprites are sized to the field scale; rebuild them if it changed.
    if (this.geo.pxPerYard !== before.pxPerYard || this.geo.spriteScale !== before.spriteScale) {
      this.buildUnits();
    }
    this.draw(this.clock.t, 0);
    this.follow(1);
  }

  /**
   * Every sprite where the log puts it at sim time t, in the pose the log gives it, its loop
   * advanced by `deltaMs` of real time; then the ball, and the name plate if one is up.
   */
  private draw(t: number, deltaMs: number): void {
    const { units, ball } = this.timeline;
    for (const [unit, s] of this.sprites) {
      const p = this.geo.toPx(positionAt(units.get(unit) as Track, t));
      const pose = poseAt(this.poses, unit, t);
      let bob = 0;
      if (s.loop !== null && !this.reduced) {
        s.loop.clockMs += deltaMs * this.clock.speed * LOOP_RATE[pose];
        s.body.setFrame(String(frameAt(s.loop.sequence, s.loop.clockMs)));
        if (pose === 'run') bob = Math.floor(t * BOB_PER_SECOND * 2) % 2 === 0 ? 0 : -s.scale;
      }
      s.body.y = s.baseY + bob;
      s.container
        .setPosition(p.x + this.fx.jostle(unit, t), p.y)
        .setDepth(UNIT_DEPTH + p.y / this.geo.height);
    }
    const ground: Spot = ballAt(ball, units, t);
    const g = this.geo.toPx(ground);
    const flight = flightAt(ball, t);
    const lift =
      flight === null
        ? 0
        : Math.round(
            Math.sin(Math.PI * flight.f) *
              Math.min(ARC_MAX_YARDS, flight.yards * ARC_PER_YARD) *
              this.geo.pxPerYard,
          );
    this.ball.shadow.setPosition(g.x, g.y).setVisible(lift > 0);
    this.ball.ball.setPosition(g.x, g.y - lift);
    this.ballProxy.x = g.x;
    this.ballProxy.y = g.y;
    this.placePlate();
  }
}
